/**
 * Minimal i18n — one provider, one `useT()` hook, dictionaries in en.ts /
 * de.ts. Deliberately no i18n library: two locales and flat keys don't
 * justify the dependency; the de-coverage test keeps dictionaries honest.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { de } from "./de";
import { en, type MessageKey } from "./en";

export type Locale = "en" | "de";

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { en, de };

const STORAGE_KEY = "discentem.locale";

export function detectInitialLocale(stored: string | null, navigatorLang: string): Locale {
  if (stored === "en" || stored === "de") return stored;
  return navigatorLang.toLowerCase().startsWith("de") ? "de" : "en";
}

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? detectInitialLocale(localStorage.getItem(STORAGE_KEY), navigator.language),
  );

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => {
      let msg: string = DICTIONARIES[locale][key] ?? en[key];
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          msg = msg.replaceAll(`{${k}}`, String(v));
        }
      }
      return msg;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
